import { createContext, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { removeThemeCss } from "../utils/themeCss";
import {
  applyMasterThemeAssets,
  getCachedThemeData,
  getLoginRedirectPath,
} from "../utils/themeCache";
//import { applyTenantPwaFromTheme } from "../utils/tenantPwa";

const RoleThemeContext = createContext();

const ADMIN_MASTER_THEME_LINK_ID = "admin-master-theme";

export function RoleThemeProvider({ children }) {
  const { adminUser } = useAuth();

  // Define role-based themes
  const roleThemes = {
    // Admin role theme - Professional Blue
    A: {
      name: "Admin Theme",
      role: "Admin",
      colors: {
        sidebar: {
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)",
          hover: "#3b82f6",
          active: "#2563eb",
          text: "#ffffff",
          textMuted: "#cbd5e1"
        },
        header: {
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)",
          text: "#ffffff"
        },
        buttons: {
          primary: "bg-blue-600 hover:bg-blue-700",
          secondary: "bg-gray-600 hover:bg-gray-700"
        },
        accents: {
          success: "#10b981",
          warning: "#f59e0b",
          error: "#ef4444",
          info: "#3b82f6"
        },
        border: {
          primary: "#2563eb",
          light: "#bfdbfe"
        }
      },
      gradients: {
        primary: "from-blue-50 to-indigo-50",
        secondary: "from-blue-100 to-blue-200"
      },
      gradientBg: {
        primary: "linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)",
        secondary: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
      }
    },

    // Master role theme - uses global variables from custom.css (extended for role UI)
    M: {
      name: "Master Theme",
      role: "Master",
      colors: {
        sidebar: {
          background: "var(--theme-sidebar-bg)",
          hover: "var(--color-primary-hover)",
          active: "var(--color-primary-active)",
          text: "var(--theme-sidebar-text)",
          textMuted: "var(--theme-sidebar-text-muted)"
        },
        header: {
          background: "var(--theme-header-bg)",
          text: "var(--theme-sidebar-text)"
        },
        buttons: {
          primary: "bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)]",
          secondary: "bg-[var(--color-text-body)] hover:opacity-90"
        },
        accents: {
          success: "var(--success-color)",
          warning: "var(--warning-color)",
          error: "var(--error-color)",
          info: "var(--info-color)"
        },
        border: {
          primary: "var(--color-primary)",
          light: "var(--color-primary-muted)"
        }
      },
      gradients: {
        primary: "from-[var(--color-bg-light)] to-[var(--color-primary-muted)]",
        secondary: "from-[var(--color-primary-light)] to-[var(--color-primary)]/20"
      },
      gradientBg: {
        primary: "linear-gradient(135deg, var(--color-bg-light) 0%, var(--color-primary-muted) 100%)",
        secondary: "linear-gradient(135deg, var(--color-primary-light) 0%, rgba(138, 156, 106, 0.25) 100%)"
      }
    }
  };

  const [roleTheme, setRoleTheme] = useState(null);
  const [masterThemeData, setMasterThemeData] = useState(null);

  const roleKey = adminUser?.role || adminUser?.userType;
  const isMasterRole = roleKey === "M";
  const masterThemeCache = isMasterRole && adminUser ? getCachedThemeData() : null;

  useEffect(() => {
    if (!adminUser) {
      setRoleTheme(null);
      setMasterThemeData(null);
      removeThemeCss({ id: ADMIN_MASTER_THEME_LINK_ID });
      return;
    }

    if (roleKey === "A" || roleKey === "M") {
      const theme = roleThemes[roleKey] || roleThemes.A;
      setRoleTheme(theme);

      if (roleKey === "M") {
        const cachedTheme = getCachedThemeData();
        if (!cachedTheme) {
          setMasterThemeData(null);
          removeThemeCss({ id: ADMIN_MASTER_THEME_LINK_ID });
          return;
        }

        setMasterThemeData(cachedTheme);
        applyMasterThemeAssets(
          cachedTheme,
          ADMIN_MASTER_THEME_LINK_ID,
          Date.now(),
        );
      } else {
        setMasterThemeData(null);
        removeThemeCss({ id: ADMIN_MASTER_THEME_LINK_ID });
      }
    }

    return () => removeThemeCss({ id: ADMIN_MASTER_THEME_LINK_ID });
  // roleThemes is intentionally omitted: it's a static map; effect should run only when admin user/role changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUser?.role, adminUser?.userType, adminUser]);

  if (isMasterRole && adminUser && !masterThemeCache) {
    return <Navigate to={getLoginRedirectPath()} replace />;
  }

  const value = {
    roleTheme,
    isAdmin: (adminUser?.role || adminUser?.userType) === "A",
    isMaster: (adminUser?.role || adminUser?.userType) === "M",
    roleThemes,
    masterThemeData
  };

  return (
    <RoleThemeContext.Provider value={value}>
      {children}
    </RoleThemeContext.Provider>
  );
}

export const useRoleTheme = () => {
  const context = useContext(RoleThemeContext);
  if (!context) {
    throw new Error("useRoleTheme must be used within RoleThemeProvider");
  }
  return context;
};